import { Button } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot } from 'firebase/firestore';
import React, { FC, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from 'src/config';

import { ContentLoadError, DataTable, PageTitle, PageWrapper } from '../../../../components';
import {
  useGetAttractionCollectionFirebase,
} from '../../../../queries';
import type { ResponseAttractionDataProps } from '../../../../queries/attractionQueryFirebase/useGetAttractionCollectionFirebase';
import { AttractionColumns } from './columns';

const PAGE_SIZE = 20;

const normalize = (value: unknown): string => (typeof value === 'string' ? value.toLowerCase() : '');

const matchesSearch = (attraction: ResponseAttractionDataProps, needle: string): boolean => {
  if (!needle) return true;
  const titleValues = attraction.title
    ? Object.values(attraction.title).map(normalize)
    : [];
  const haystacks = [
    ...titleValues,
    normalize(attraction.city),
    normalize(attraction.address),
    normalize(attraction.type),
    normalize(attraction.email),
    normalize(attraction.createdBy),
    normalize(attraction.status),
    normalize(attraction.translationStatus),
  ];
  return haystacks.some((h) => h.includes(needle));
};

export const Attractions: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? '1');
  const searchTerm = searchParams.get('q') ?? '';

  const queryClient = useQueryClient();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'attractions'), () => {
      // @ts-ignore
      queryClient.invalidateQueries(['attractions']);
    });

    return () => unsub();
  }, [queryClient]);

  const { data: attractions, error, isFetching, refetch } = useGetAttractionCollectionFirebase();

  const { pageData, totalPages } = useMemo(() => {
    const all = attractions ?? [];
    const needle = searchTerm.trim().toLowerCase();
    const filtered = needle ? all.filter((a) => matchesSearch(a, needle)) : all;
    const total = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(Math.max(1, currentPage), total);
    const start = (safePage - 1) * PAGE_SIZE;
    return {
      pageData: filtered.slice(start, start + PAGE_SIZE),
      totalPages: total,
    };
  }, [attractions, searchTerm, currentPage]);

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('q', value);
      } else {
        next.delete('q');
      }
      next.set('page', '1');
      return next;
    });
  };

  if (error) {
    return (
      <PageWrapper>
        <ContentLoadError />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageTitle
        title="Attraction"
        actions={(
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              color="secondary"
              radius="sm"
              onPress={() => navigate('/attractions/new')}
            >
              Add new
            </Button>
          </div>
        )}
      />
      <DataTable
        data={pageData}
        columns={AttractionColumns}
        isLoading={isFetching}
        isError={!!error}
        onRefresh={refetch}
        pageSize={PAGE_SIZE}
        searchPlaceholder="Search by title, city, type, created by..."
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
      />
    </PageWrapper>
  );
};
